import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Deposit.css";
import InnerHeader from "../../components/InnerHeader";
import useTranslate from "../../hooks/useTranslate";
import UserBalanceHero from "../../components/UserBalanceHero/UserBalanceHero";

const dplistMoney = (value) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const dplistDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const dplistPromoId = (promo) => promo?.id ?? promo?.promotion_id ?? null;

const dplistPromoLabel = (promo, t) =>
  promo?.title ||
  promo?.name ||
  promo?.promotion_name ||
  promo?.promotionTitle ||
  t("participateInPromotion");

export default function Deposit({
  items = [],
  promotions = [],
  userData,
  transactionData = [],
  hasEWallet,
  usdtRate,
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslate();

  const [dplistActiveTab, setDplistActiveTab] = useState("deposit");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentSystems, setPaymentSystems] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const [activeAmount, setActiveAmount] = useState("100");
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [promotionDraftId, setPromotionDraftId] = useState(null);

  const [showPromoModal, setShowPromoModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [wapy, setWapy] = useState(false);

  const [getotherContracts, setotherContracts] = useState([]);
  const [amountRange, setAmountRange] = useState([100, 25000]);
  const [currency, setCurrency] = useState("৳");
  const [presetAmounts, setPresetAmounts] = useState([
    "100",
    "500",
    "1000",
    "2000",
    "3000",
    "5000",
    "10000",
    "15000",
    "20000",
    "25000",
  ]);
  const [depositChannel, setDepositChannel] = useState(true);

  const pgs = useMemo(() => {
    const unique = [];
    const seen = new Set();

    (items || []).forEach((item) => {
      const key = (item?.name || "").trim().toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        unique.push(item);
      }
    });

    return unique;
  }, [items]);

  const selectedPaymentSystem = useMemo(() => {
    return (
      paymentSystems.find(
        (item) => String(item?.id) === String(selectedOption)
      ) || paymentSystems[0] || null
    );
  }, [paymentSystems, selectedOption]);

  const normalizedTransactionData = useMemo(() => {
    if (Array.isArray(transactionData)) return transactionData;
    if (Array.isArray(transactionData?.data)) return transactionData.data;
    if (Array.isArray(transactionData?.transactions)) return transactionData.transactions;
    if (Array.isArray(transactionData?.rows)) return transactionData.rows;
    return [];
  }, [transactionData]);

  const depositRows = useMemo(() => {
    return normalizedTransactionData
      .filter((row) => String(row?.action || "").toLowerCase() === "deposit")
      .map((row, index) => ({
        no: index + 1,
        type: row?.payment_type || row?.type || row?.method || "-",
        time: dplistDate(row?.created_at || row?.time),
        amount: row?.amount || 0,
        status: row?.status || (String(row?.key) === "0" ? "Pending" : "Approved"),
        receipt: row?.transaction || row?.trx || row?.receipt || "-",
      }));
  }, [normalizedTransactionData]);

  const username =
    userData?.username || userData?.user_name || userData?.name || "";
  const mainBalance = Number(
    userData?.balance ?? userData?.main_balance ?? userData?.wallet ?? 0
  );
  const freeEventWallet = Number(
    userData?.free_event_wallet ??
    userData?.free_wallet ??
    userData?.bonus_balance ??
    5300
  );

  const openAlert = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const fetchOther = async () => {
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/get/other/phone`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setotherContracts(data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching other phone:", error);
    }
  };

  const fetchWay = async () => {
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/wpay/check`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      setWapy(response.status === 200);
    } catch (error) {
      console.error("Error fetching WPay check:", error);
      setWapy(false);
    }
  };

  const loadChannels = async (method) => {
    if (!method?.name) {
      setPaymentSystems([]);
      setSelectedOption(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      if (hasEWallet === true) {
        const filtered = (items || []).filter(
          (item) =>
            String(item?.name || "").toLowerCase() ===
            String(method?.name || "").toLowerCase()
        );

        setPaymentSystems(filtered);
        setSelectedOption(filtered[0]?.id ?? null);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/showpayment-menthod?name=${encodeURIComponent(
          method.name
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Origin: window.location.origin,
          },
        }
      );

      const data = await response.json();

      if (data?.success && Array.isArray(data?.data) && data.data.length > 0) {
        setPaymentSystems(data.data);
        setSelectedOption(data.data[0]?.id ?? null);
      } else {
        const fallback = (items || []).filter(
          (item) =>
            String(item?.name || "").toLowerCase() ===
            String(method?.name || "").toLowerCase()
        );
        setPaymentSystems(fallback);
        setSelectedOption(fallback[0]?.id ?? null);
      }
    } catch (error) {
      console.error("Error fetching payment method details:", error);
      const fallback = (items || []).filter(
        (item) =>
          String(item?.name || "").toLowerCase() ===
          String(method?.name || "").toLowerCase()
      );
      setPaymentSystems(fallback);
      setSelectedOption(fallback[0]?.id ?? null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOther();
    fetchWay();
  }, []);

  useEffect(() => {
    if (!paymentMethod && pgs.length > 0) {
      setPaymentMethod(pgs[0]);
    }
  }, [pgs, paymentMethod]);

  useEffect(() => {
    if (
      promotions.length > 0 &&
      (!selectedPromotion ||
        !promotions.some(
          (promo) =>
            String(dplistPromoId(promo)) ===
            String(dplistPromoId(selectedPromotion))
        ))
    ) {
      setSelectedPromotion(promotions[0]);
    }
  }, [promotions, selectedPromotion]);

  useEffect(() => {
    if (paymentMethod) {
      loadChannels(paymentMethod);
    }
  }, [paymentMethod, hasEWallet, token, items]);

  useEffect(() => {
    if (selectedPaymentSystem?.account_type === "USDT") {
      setAmountRange([10, 200]);
      setCurrency("$");
      setPresetAmounts(["10", "20", "30", "50", "100", "150", "200"]);
      setDepositChannel(false);
      if (!activeAmount) setActiveAmount("10");
    } else {
      setAmountRange([100, 25000]);
      setCurrency("৳");
      setPresetAmounts([
        "100",
        "500",
        "1000",
        "2000",
        "3000",
        "5000",
        "10000",
        "15000",
        "20000",
        "25000",
      ]);
      setDepositChannel(true);
      if (!activeAmount) setActiveAmount("100");
    }
  }, [selectedPaymentSystem]);

  const handleItemClick = (methodName) => {
    const selectedMethod = pgs.find((item) => item?.name === methodName);
    if (selectedMethod) {
      setPaymentMethod(selectedMethod);
    }
  };

  const handleSubmit = async () => {
    const numericAmount = Number(activeAmount);

    if (!activeAmount || Number.isNaN(numericAmount)) {
      openAlert(t("pleaseFillAmount"));
      return;
    }

    if (numericAmount < amountRange[0] || numericAmount > amountRange[1]) {
      openAlert(
        t("amountMustBeBetween", {
          currency,
          min: amountRange[0],
          max: amountRange[1],
        })
      );
      return;
    }

    if (!paymentMethod) {
      openAlert(t("pleaseSelectValidPaymentMethod"));
      return;
    }

    if (!selectedPaymentSystem) {
      openAlert(t("pleaseSelectDepositChannel"));
      return;
    }

    setSubmitLoading(true);

    try {
      if (hasEWallet) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/ewa/request`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                Origin: window.location.origin,
              },
              body: JSON.stringify({
                amount: activeAmount,
                type: selectedPaymentSystem?.account_type,
                provider: paymentMethod?.name,
                promotion: dplistPromoId(selectedPromotion) || 0,
              }),
            }
          );

          const data = await response.json();

          if (response.ok) {
            if (data?.url) {
              window.location.href = data.url;
              return;
            }

            navigate("/make-payment", {
              state: {
                amount: data?.amount ?? activeAmount,
                payment_icon: selectedPaymentSystem?.icon,
                payment_type: selectedPaymentSystem?.account_type,
                paymentMethodid: paymentMethod?.id,
                paymentMethodName: paymentMethod?.name,
                promotion: dplistPromoId(selectedPromotion) || null,
                transaction: data?.transaction,
                number: data?.number,
                timer: data?.timer ?? 10,
                message: data?.message,
              },
            });
          } else {
            openAlert(data?.message || t("anErrorOccurredPleaseTryAgain"));
          }
        } catch (error) {
          console.error("Error fetching e-wallet request:", error);
          openAlert(t("anErrorOccurredPleaseTryAgain"));
        }
      } else if (wapy) {
        const params = new URLSearchParams({
          amount: activeAmount,
          method: String(paymentMethod?.name || "").toUpperCase(),
        });

        if (dplistPromoId(selectedPromotion)) {
          params.append("promotion", dplistPromoId(selectedPromotion));
        }

        const url = `${process.env.REACT_APP_API_URL}/api/payin?${params.toString()}`;

        try {
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          });

          const data = await response.json();

          if (data?.[0]?.data?.url) {
            window.open(data[0].data.url, "_blank");
          } else {
            openAlert(t("unableToStartPayment"));
          }
        } catch (error) {
          console.error("Error calling PayIn API:", error);
          openAlert(t("anErrorOccurredPleaseTryAgain"));
        }
      } else {
        navigate("/make-payment", {
          state: {
            amount: activeAmount,
            payment_icon: selectedPaymentSystem?.icon,
            payment_type: selectedPaymentSystem?.account_type,
            paymentMethodid: paymentMethod?.id,
            paymentMethodName: paymentMethod?.name,
            promotion: dplistPromoId(selectedPromotion) || null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      openAlert(t("anErrorOccurredPleaseTryAgain"));
    } finally {
      setSubmitLoading(false);
    }
  };

  const openPromotionModal = () => {
    setPromotionDraftId(dplistPromoId(selectedPromotion) || dplistPromoId(promotions[0]));
    setShowPromoModal(true);
  };

  const confirmPromotion = () => {
    const promo =
      promotions.find(
        (item) => String(dplistPromoId(item)) === String(promotionDraftId)
      ) || null;

    setSelectedPromotion(promo);
    setShowPromoModal(false);
  };

  const headerTitle =
    dplistActiveTab === "deposit" ? t("deposit") : t("depositList");

  return (
    <div className="">
      <div className="dplist-shell">
        <InnerHeader title={headerTitle} />

        <UserBalanceHero />

        <div className="dplist-tab-row">
          <button
            type="button"
            className={`dplist-tab-btn ${dplistActiveTab === "deposit" ? "dplist-tab-btn--active" : ""
              }`}
            onClick={() => setDplistActiveTab("deposit")}
          >
            {t("deposit")}
          </button>

          <button
            type="button"
            className={`dplist-tab-btn ${dplistActiveTab === "list" ? "dplist-tab-btn--active" : ""
              }`}
            onClick={() => setDplistActiveTab("list")}
          >
            {t("depositList")}
          </button>
        </div>

        {dplistActiveTab === "deposit" ? (
          <div className="dplist-content">
            <div className="dplist-section-label">{t("paymentMethods")}</div>

            <div className="dplist-methods-grid">
              {pgs.map((method) => {
                const active =
                  String(paymentMethod?.name || "").toLowerCase() ===
                  String(method?.name || "").toLowerCase();

                return (
                  <button
                    key={method?.id || method?.name}
                    type="button"
                    className={`dplist-method-card ${active ? "dplist-method-card--active" : ""
                      }`}
                    onClick={() => handleItemClick(method?.name)}
                  >
                    {method?.icon ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL}/storage/${method.icon}`}
                        alt={method?.name}
                        className="dplist-method-img"
                      />
                    ) : (
                      <span className="dplist-method-fallback">
                        {method?.name}
                      </span>
                    )}

                    {active && <span className="dplist-corner-check">✓</span>}
                  </button>
                );
              })}
            </div>

            {depositChannel && (
              <>
                <div className="dplist-section-label">{t("selectChannel")}</div>

                <div className="dplist-channel-grid">
                  {isLoading ? (
                    <div className="dplist-loading-box">{t("loadingChannels")}</div>
                  ) : (
                    paymentSystems.map((system) => {
                      const active =
                        String(selectedOption) === String(system?.id);

                      return (
                        <button
                          key={system?.id}
                          type="button"
                          className={`dplist-channel-btn ${active ? "dplist-channel-btn--active" : ""
                            }`}
                          onClick={() => setSelectedOption(system?.id)}
                        >
                          {system?.name ||
                            system?.account_name ||
                            system?.channel ||
                            system?.account_type ||
                            t("channel")}

                          {active && (
                            <span className="dplist-corner-check">✓</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}

            <div className="dplist-amount-wrap">
              <span className="dplist-currency">{currency}</span>
              <input
                type="number"
                className="dplist-amount-input"
                value={activeAmount}
                min={amountRange[0]}
                max={amountRange[1]}
                onChange={(e) => setActiveAmount(e.target.value)}
              />
            </div>

            <div className="dplist-preset-grid">
              {presetAmounts.slice(0, 5).map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`dplist-preset-btn ${String(activeAmount) === String(amount)
                      ? "dplist-preset-btn--active"
                      : ""
                    }`}
                  onClick={() => setActiveAmount(String(amount))}
                >
                  {amount}
                </button>
              ))}
            </div>

            <div className="dplist-section-label">{t("promotion")}:</div>

            <button
              type="button"
              className="dplist-promo-bar"
              onClick={openPromotionModal}
            >
              <span
                className={`dplist-promo-check ${selectedPromotion ? "dplist-promo-check--active" : ""
                  }`}
              >
                {selectedPromotion ? "✓" : ""}
              </span>

              <span className="dplist-promo-text">
                {dplistPromoLabel(selectedPromotion, t)}
              </span>

              <span className="dplist-promo-arrow">›</span>
            </button>

            <button
              type="button"
              className="dplist-submit-btn"
              onClick={handleSubmit}
              disabled={submitLoading}
            >
              {submitLoading ? t("submitting") : t("submit")}
            </button>
          </div>
        ) : (
          <div className="dplist-content">
            <div className="dplist-table-wrap">
              <table className="dplist-table">
                <thead>
                  <tr>
                    <th>{t("no")}</th>
                    <th>{t("type")}</th>
                    <th>{t("time")}</th>
                    <th>{t("amount")}</th>
                    <th>{t("status")}</th>
                    <th>{t("receipt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {depositRows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="dplist-empty-cell">
                        {t("noRecordFound")}
                      </td>
                    </tr>
                  ) : (
                    depositRows.map((row) => (
                      <tr key={`${row.receipt}-${row.no}`}>
                        <td>{row.no}</td>
                        <td>{row.type}</td>
                        <td>{row.time}</td>
                        <td>{dplistMoney(row.amount)}</td>
                        <td>{row.status}</td>
                        <td>{row.receipt}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showPromoModal && (
          <div
            className="dplist-modal-overlay"
            onClick={() => setShowPromoModal(false)}
          >
            <div
              className="dplist-modal-card"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="dplist-modal-close"
                onClick={() => setShowPromoModal(false)}
              >
                ×
              </button>

              <h3 className="dplist-modal-title">{t("selectAPromotion")}</h3>

              <div className="dplist-modal-list">
                {promotions.map((promo) => {
                  const promoId = dplistPromoId(promo);
                  const active =
                    String(promotionDraftId) === String(promoId);

                  return (
                    <button
                      key={promoId}
                      type="button"
                      className={`dplist-modal-item ${active ? "dplist-modal-item--active" : ""
                        }`}
                      onClick={() => setPromotionDraftId(promoId)}
                    >
                      <div className="dplist-modal-left">
                        <div className="dplist-modal-name">
                          {dplistPromoLabel(promo, t)}
                        </div>

                        <span className="dplist-modal-link">
                          {t("viewPromotionDetails")}
                        </span>
                      </div>

                      <span
                        className={`dplist-modal-radio ${active ? "dplist-modal-radio--active" : ""
                          }`}
                      />
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="dplist-confirm-btn"
                onClick={confirmPromotion}
              >
                {t("confirm")}
              </button>
            </div>
          </div>
        )}

        {showModal && (
          <div className="dplist-alert-overlay" onClick={() => setShowModal(false)}>
            <div
              className="dplist-alert-card"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="dplist-alert-text">{modalMessage}</p>
              <button
                type="button"
                className="dplist-alert-btn"
                onClick={() => setShowModal(false)}
              >
                {t("ok")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}